import { useEffect, useState, useRef } from "react";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import LanguageSelector from "@/components/LanguageSelector";
import { useI18n } from "@/lib/i18n";

/**
 * Top-level nav items. Order matters.
 * type='scroll' → smooth-scroll to a section ID on the landing page
 * type='link'   → React Router link (a href)
 * type='dropdown' → opens a panel with sub-items
 * badge='NEW' → coloured pill rendered next to label
 */
const NAV = [
  { type: "link", id: "home", label: "Home", href: "/" },
  { type: "link", id: "about", label: "About Us", href: "/about" },
  {
    type: "dropdown",
    id: "products",
    label: "Products",
    columns: [
      {
        title: "Loans",
        items: [
          { label: "Personal Loan", href: "/apply/loan" },
          { label: "Home Loan", href: "/apply/loan" },
          { label: "Business / MSME", href: "/apply/loan" },
          { label: "Loan Against Property", href: "/apply/loan" },
          { label: "Vehicle Loan", href: "/apply/loan" },
          { label: "Gold Loan", href: "/apply/loan" },
          { label: "Gold Loan for Buy", href: "/apply/loan" },
          { label: "Education Loan", href: "/apply/loan" },
          { label: "Construction Loan", href: "/apply/loan" },
          { label: "Plot Loan", href: "/apply/loan" },
          { label: "Loan Against Open Plot", href: "/apply/loan" },
          { label: "Loan Against Security Deposit", href: "/apply/loan" },
        ],
      },
      {
        title: "Rates & EMI",
        items: [
          { label: "All Loan Rates", href: "/loan-rates" },
          { label: "EMI Calculator", href: "/#emi" },
        ],
      },
      {
        title: "Investments",
        items: [
          { label: "Mutual Funds & SIP", href: "/#services" },
          { label: "Shares & Equity", href: "/#services" },
          { label: "Project Finance", href: "/#services" },
        ],
      },
      {
        title: "Insurance",
        items: [
          { label: "Loan Protection Cover", href: "/customer-services" },
          { label: "Term & Health Plans", href: "/customer-services" },
          { label: "Asset Insurance", href: "/customer-services" },
        ],
      },
    ],
  },
  { type: "link", id: "hot-offers", label: "Hot Offers", href: "/hot-offers", badge: "NEW" },
  { type: "link", id: "careers", label: "Careers", href: "/careers", badge: "EARN" },
  { type: "link", id: "partner-portal", label: "Partner Login", href: "/partner/login", wide: true },
  { type: "link", id: "investor-portal", label: "Investor Login", href: "/investor/login", wide: true },
  { type: "link", id: "referral-calculator", label: "Referral Calculator", href: "/referral-calculator", wide: true },
  {
    type: "dropdown",
    id: "more",
    label: "More",
    columns: [
      {
        title: "Portals",
        items: [
          { label: "Partner Login", href: "/partner/login" },
          { label: "Investor Login", href: "/investor/login" },
          { label: "Referral Calculator", href: "/referral-calculator" },
        ],
      },
      {
        title: "Company",
        items: [
          { label: "Investors", href: "/investors" },
          { label: "CSR", href: "/csr" },
          { label: "Leadership", href: "/leadership" },
          { label: "Vision & Mission", href: "/vision" },
        ],
      },
      {
        title: "Resources",
        items: [
          { label: "Blogs", href: "/blogs" },
          { label: "Customer Services", href: "/customer-services" },
          { label: "Grievance", href: "/grievance" },
          { label: "Training", href: "/training" },
        ],
      },
      {
        title: "Trust & Compliance",
        items: [
          { label: "Governance", href: "/governance" },
          { label: "Policies", href: "/policies" },
          { label: "Privacy Policy", href: "/privacy" },
          { label: "Terms & Conditions", href: "/terms" },
        ],
      },
    ],
  },
];

export default function Navbar() {
  const { t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const dropdownTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleScrollTo = (id) => {
    setOpen(false);
    setDropdownOpen(null);
    if (window.location.pathname !== "/") {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openDropdown = (id) => {
    if (dropdownTimer.current) clearTimeout(dropdownTimer.current);
    setDropdownOpen(id);
  };
  const scheduleClose = () => {
    dropdownTimer.current = setTimeout(() => setDropdownOpen(null), 180);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-md bg-[#04102B]/90 border-b border-[#D4AF37]/20"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 xl:px-10 2xl:px-14 h-20 md:h-[104px] xl:h-[116px] 2xl:h-[132px] flex items-center justify-between gap-3 md:gap-4">
        {/* Logo */}
        <a
          data-testid="nav-logo"
          href="/"
          aria-label="Sahyog Finance & Noble Infinity — Home"
          className="flex items-center gap-3 md:gap-4 shrink-0 min-w-0"
        >
          <Logo size={60} className="md:!w-[88px] md:!h-[88px] xl:!w-[88px] xl:!h-[88px] 2xl:!w-[116px] 2xl:!h-[116px]" testId="nav-logo-mark" />
          <div className="text-left leading-tight hidden md:block">
            <div className="font-display text-white text-lg 2xl:text-2xl min-[1800px]:text-3xl tracking-tight uppercase whitespace-nowrap font-semibold">
              SAHYOG FINANCE
              <br />
              <span className="text-[#D4AF37]">&amp; NOBLE INFINITY</span>
            </div>
            <div className="font-display text-[#D4AF37] text-[10px] xl:text-[11px] 2xl:text-sm tracking-[0.18em] uppercase mt-1.5">
              Asset Management Pvt. Ltd.
            </div>
          </div>
        </a>

        {/* Desktop nav */}
        <nav
          className="hidden xl:flex items-center gap-0.5 2xl:gap-1 flex-1 justify-center min-w-0"
          aria-label="Primary"
        >
          {NAV.map((n) => {
            if (n.type === "dropdown") {
              const isOpen = dropdownOpen === n.id;
              return (
                <div
                  key={n.id}
                  className="relative"
                  onMouseEnter={() => openDropdown(n.id)}
                  onMouseLeave={scheduleClose}
                >
                  <button
                    data-testid={`nav-link-${n.id}`}
                    onClick={() => setDropdownOpen(isOpen ? null : n.id)}
                    className="nav-link relative font-semibold text-white/90 hover:text-[#F0D788] transition-colors py-2.5 px-2 2xl:px-4 inline-flex items-center gap-1.5 whitespace-nowrap"
                  >
                    {t(`nav.${n.id}`, n.label)}
                    <ChevronDown size={15} className={`transition-transform ${isOpen ? "rotate-180 text-[#F0D788]" : ""}`} />
                  </button>
                  {isOpen && (
                    <div
                      data-testid={`nav-dropdown-${n.id}`}
                      className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 ${n.columns.length >= 5 ? "w-[920px]" : "w-[680px]"} max-w-[calc(100vw-2rem)] rounded-2xl border border-[#D4AF37]/30 bg-[#04102B]/98 backdrop-blur-xl shadow-2xl p-6 grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-5`}
                      onMouseEnter={() => openDropdown(n.id)}
                      onMouseLeave={scheduleClose}
                    >
                      {n.columns.map((col) => (
                        <div key={col.title}>
                          <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] font-semibold">
                            {col.title}
                          </div>
                          <ul className="mt-3 space-y-2">
                            {col.items.map((it) => (
                              <li key={it.label}>
                                <a
                                  data-testid={`nav-dd-${col.title.toLowerCase().replace(/\s+/g, "-")}-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                                  href={it.href}
                                  className="block text-sm text-white/75 hover:text-[#F0D788] hover:translate-x-0.5 transition-all"
                                >
                                  {it.label}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            if (n.type === "scroll") {
              return (
                <button
                  key={n.id}
                  data-testid={`nav-link-${n.id}`}
                  onClick={() => handleScrollTo(n.id)}
                  className="nav-link relative font-semibold text-white/90 hover:text-[#F0D788] transition-colors py-2.5 px-2 2xl:px-4 whitespace-nowrap"
                >
                  {t(`nav.${n.id}`, n.label)}
                </button>
              );
            }
            return (
              <a
                key={n.id}
                data-testid={`nav-link-${n.id}`}
                href={n.href}
                className={`nav-link relative font-semibold text-white/90 hover:text-[#F0D788] transition-colors py-2.5 px-2 2xl:px-4 items-center gap-2 whitespace-nowrap ${n.wide ? "hidden" : "inline-flex"}`}
              >
                {t(`nav.${n.id}`, n.label)}
                {n.badge && (
                  <span className="px-2 py-0.5 rounded-full bg-rose-500 text-white text-[11px] font-bold tracking-wider leading-none animate-pulse">
                    {n.badge}
                  </span>
                )}
              </a>
            );
          })}
        </nav>

        {/* CTA */}
        <div className="hidden xl:flex items-center gap-2 shrink-0">
          <LanguageSelector testId="nav-lang-selector" />
          <a
            data-testid="nav-call-cta"
            href="tel:+917016975324"
            className="hidden min-[1800px]:inline-flex items-center gap-2 px-4 py-2.5 rounded-full btn-outline-gold text-sm whitespace-nowrap"
          >
            <Phone size={14} /> 70169 75324
          </a>
          <a
            data-testid="nav-apply-cta"
            href="/apply/loan"
            className="px-4 py-2 2xl:px-5 2xl:py-2.5 rounded-full btn-gold text-xs 2xl:text-sm font-semibold whitespace-nowrap"
          >
            {t("nav.apply")}
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="mobile-nav-panel"
          aria-label={open ? "Close navigation menu" : "Open navigation menu"}
          className="xl:hidden text-white p-3 rounded-md border border-[#D4AF37]/30 min-w-[44px] min-h-[44px] inline-flex items-center justify-center"
        >
          {open ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
        </button>
      </div>

      {/* Mobile panel */}
      {open && (
        <div
          data-testid="nav-mobile-panel"
          id="mobile-nav-panel"
          role="dialog"
          aria-modal="true"
          aria-label="Main navigation"
          className="xl:hidden bg-[#04102B] border-t border-[#D4AF37]/20 px-5 py-6 max-h-[85vh] overflow-y-auto"
        >
          <nav aria-label="Mobile primary" className="flex flex-col gap-1">
            {NAV.map((n) => {
              if (n.type === "dropdown") {
                const isOpen = dropdownOpen === n.id;
                return (
                  <div key={n.id}>
                    <button
                      data-testid={`nav-mobile-link-${n.id}`}
                      onClick={() => setDropdownOpen(isOpen ? null : n.id)}
                      aria-expanded={isOpen}
                      className="w-full flex items-center justify-between text-left text-white/90 text-base py-3 border-b border-white/5"
                    >
                      <span className="inline-flex items-center gap-2">{t(`nav.${n.id}`, n.label)}</span>
                      <ChevronDown size={16} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    {isOpen && (
                      <div className="pl-4 py-3 space-y-4 border-b border-white/5">
                        {n.columns.map((col) => (
                          <div key={col.title}>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-1.5">{col.title}</div>
                            <ul className="space-y-1">
                              {col.items.map((it) => (
                                <li key={it.label}>
                                  <a href={it.href} className="block text-sm text-white/75 py-2 min-h-[40px]">{it.label}</a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              if (n.type === "scroll") {
                return (
                  <button
                    key={n.id}
                    data-testid={`nav-mobile-link-${n.id}`}
                    onClick={() => handleScrollTo(n.id)}
                    className="text-left text-white/90 text-base py-3 border-b border-white/5"
                  >
                    {t(`nav.${n.id}`, n.label)}
                  </button>
                );
              }
              return (
                <a
                  key={n.id}
                  data-testid={`nav-mobile-link-${n.id}`}
                  href={n.href}
                  className="flex items-center gap-2 text-white/90 text-base py-3 border-b border-white/5 min-h-[48px]"
                >
                  {t(`nav.${n.id}`, n.label)}
                  {n.badge && (
                    <span className="px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[9px] font-bold tracking-wider keep-nowrap">
                      {n.badge}
                    </span>
                  )}
                </a>
              );
            })}
            <div className="mt-3 flex justify-center">
              <LanguageSelector testId="nav-mobile-lang-selector" />
            </div>
            <a
              href="tel:+917016975324"
              data-testid="nav-mobile-call"
              className="mt-2 text-center px-4 py-3 rounded-full btn-outline-gold text-sm"
            >
              <Phone size={14} className="inline mr-2" />
              70169 75324
            </a>
            <a
              href="/apply/loan"
              data-testid="nav-mobile-apply"
              className="px-5 py-3 rounded-full btn-gold text-sm font-semibold text-center"
            >
              {t("nav.apply")}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
